/** The signed-in person's notification inbox - ADR-0092, phase 1 (in-app only).
 *
 * **Three reads and one write, and nothing here builds a sentence.** The text of a notification is
 * built by the server at read time from ids the caller may see, so this module only validates the
 * shape that arrives and hands it on. A client that re-rendered the words from `kind` would be a
 * second home for a sentence the server already decided (design rule 7), and would be the one place
 * a record's title could leak to somebody who may no longer see it.
 *
 * **A response is checked, not trusted.** `readUnreadCount` throws on anything but a non-negative
 * integer, so a stub or a proxy answering `[]` or `{}` is a *failed* read (drawn `?`) and never a
 * quiet zero. Marking every notification read is one request of its own (G-Wapprover-29), never a
 * loop of per-row writes: a loop that failed halfway would leave the count saying something no
 * single request could be held to.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type NotificationItem = components["schemas"]["NotificationOut"];
export type NotificationList = components["schemas"]["NotificationListOut"];

/** How many the list asks for. Ours, not the server's default, so "the newest N" is a sentence the
 * screen can stand behind. The API's own ceiling is 100. */
export const NOTIFICATION_PAGE = 50;

/** How often the bell re-reads its count while the app is open. The count is one indexed `COUNT`
 * for one person; a minute is the cost of not holding a stale number for a whole session. */
export const UNREAD_POLL_MS = 60_000;

export const notificationKeys = {
  all: ["notifications"] as const,
  unread: () => ["notifications", "unread-count"] as const,
  list: () => ["notifications", "list", NOTIFICATION_PAGE] as const,
};

const KINDS: readonly string[] = [
  "approval.awaiting_you",
  "approval.decided_for_you",
  "task.assigned_to_you",
  "period.closed",
  "period.reopened",
  "period.changed_after_close",
  // ADR-0131: told by the calendar rather than by a write.
  "filing.deadline_approaching",
  "source.gap_open",
  // ADR-0229: an assurance request was raised (told to Admins and Approvers) or answered (told to the
  // Auditor who asked).
  "assurance.raised",
  "assurance.answered",
  // ADR-0223: a task's due date, told by the calendar to the task's owner.
  "task.due_soon",
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

export function readUnreadCount(value: unknown): number {
  if (!isRecord(value) || !isCount(value.unread)) {
    throw new Error("Your unread notification count could not be read.");
  }
  return value.unread;
}

function readItem(value: unknown): NotificationItem {
  if (
    !isRecord(value) ||
    typeof value.id !== "string" ||
    typeof value.kind !== "string" ||
    !KINDS.includes(value.kind) ||
    typeof value.text !== "string" ||
    value.text === "" ||
    !(value.link === null || (typeof value.link === "string" && isSafePath(value.link))) ||
    typeof value.created_at !== "string" ||
    !(value.read_at === null || typeof value.read_at === "string")
  ) {
    throw new Error("Your notifications could not be read.");
  }
  return value as unknown as NotificationItem;
}

/** A link the API serves is a path inside this app. Anything else - another origin, a protocol-
 * relative address, a script - is treated as a malformed notification rather than followed. */
export function isSafePath(link: string): boolean {
  if (!link.startsWith("/") || link.startsWith("//") || link.includes("\\")) return false;
  for (let at = 0; at < link.length; at += 1) {
    if (link.charCodeAt(at) < 0x20) return false;
  }
  return true;
}

export function readNotificationList(value: unknown): NotificationList {
  if (!isRecord(value) || !Array.isArray(value.items) || !isCount(value.unread)) {
    throw new Error("Your notifications could not be read.");
  }
  return { items: value.items.map(readItem), unread: value.unread };
}

/** The bell's number. Keyed apart from the list so the bell never waits on, or is blocked by, the
 * larger read. */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unread(),
    retry: false,
    refetchInterval: UNREAD_POLL_MS,
    queryFn: async (): Promise<number> => {
      const { data, error, response } = await api.GET("/api/v1/notifications/unread-count", {});
      if (!response.ok) {
        throw refusalFrom(response, error, "Your unread notification count could not be read.");
      }
      return readUnreadCount(data);
    },
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: notificationKeys.list(),
    retry: false,
    queryFn: async (): Promise<NotificationList> => {
      const { data, error, response } = await api.GET("/api/v1/notifications", {
        params: { query: { limit: NOTIFICATION_PAGE } },
      });
      if (!response.ok) throw refusalFrom(response, error, "Your notifications could not be read.");
      return readNotificationList(data);
    },
  });
}

/** Mark every unread notification read, in one request. Same rule as marking one: the count and the
 * list are invalidated **on success only**, so a refused write leaves every row unread and the
 * number honest. The answer's counts are not trusted for the display - the list is re-read. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (): Promise<void> => {
      const { error, response } = await api.POST("/api/v1/notifications/read-all", {});
      if (!response.ok) throw refusalFrom(response, error, "Your notifications could not be marked as read.");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

/** Mark one notification read. The count and the list are invalidated **on success only**, so a
 * refused write leaves the number honest: it still counts the notification as unread. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (id: string): Promise<void> => {
      // The body is not read: the write either happened or it did not, and a response we cannot
      // parse must not turn a recorded read into a reported failure.
      const { error, response } = await api.POST(
        "/api/v1/notifications/{notification_id}/read",
        { params: { path: { notification_id: id } } },
      );
      if (!response.ok) throw refusalFrom(response, error, "That notification could not be marked as read.");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}
